import {
  AXIOME_CONNECT_API,
  buildSignPayload,
  type CosmosMsg,
} from "./types.js";

export type TokenInfo = {
  _id?: string;
  token: string;
  exists: boolean;
  account?: string;
  pubkey?: string;
  service?: string;
  ip?: string;
  address?: string;
};

export class AxiomeConnect {
  readonly apiBase: string;
  token: string | null = null;
  account: string | null = null;
  pubkey: string | null = null;

  constructor(apiBase: string = AXIOME_CONNECT_API) {
    this.apiBase = apiBase.replace(/\/$/, "");
  }

  async createToken(): Promise<TokenInfo> {
    const res = await fetch(`${this.apiBase}/connect/create_token`);
    if (!res.ok) throw new Error(`create_token failed: ${res.status}`);
    const info = (await res.json()) as TokenInfo;
    this.token = info.token;
    return info;
  }

  authQrPayload(token: string = this.token ?? ""): string {
    if (!token) throw new Error("No token");
    return `axm:auth:token:${token}`;
  }

  async getTokenInfo(token: string = this.token ?? ""): Promise<TokenInfo> {
    if (!token) throw new Error("No token");
    const res = await fetch(`${this.apiBase}/connect/get_token_info`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) throw new Error(`get_token_info failed: ${res.status}`);
    return (await res.json()) as TokenInfo;
  }

  async waitForApproval(opts?: {
    token?: string;
    intervalMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
  }): Promise<string> {
    const token = opts?.token ?? this.token;
    if (!token) throw new Error("No token");
    const intervalMs = opts?.intervalMs ?? 2500;
    const timeoutMs = opts?.timeoutMs ?? 5 * 60 * 1000;
    const started = Date.now();

    while (Date.now() - started < timeoutMs) {
      if (opts?.signal?.aborted) throw new Error("Aborted");
      const info = await this.getTokenInfo(token);
      if (!info.exists) throw new Error("Token expired or revoked");
      if (info.account) {
        this.token = token;
        this.account = info.account;
        this.pubkey = info.pubkey ?? null;
        return info.account;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error("Connection timeout");
  }

  async verifySession(token: string = this.token ?? ""): Promise<string | null> {
    const info = await this.getTokenInfo(token);
    if (!info.exists || !info.account) return null;
    this.token = token;
    this.account = info.account;
    this.pubkey = info.pubkey ?? null;
    return info.account;
  }

  async sign(messages: CosmosMsg[], token: string = this.token ?? ""): Promise<unknown> {
    if (!token) throw new Error("Not connected");
    const payload = buildSignPayload(messages);
    const res = await fetch(`${this.apiBase}/connect/sign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ payload }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`sign failed: ${res.status} ${text}`);
    }
    return res.json().catch(() => ({ ok: true }));
  }

  disconnect() {
    this.token = null;
    this.account = null;
    this.pubkey = null;
  }
}
